/**
 * API key data scope (backend flag `scoped_api_keys`, commercial home
 * `customApi`). Mirrors `src/types/apiKeyDataScope.ts`; the preset list and
 * plan rules come from `GET /api-keys/scope-options`, not from here.
 */
export type ApiKeyScopePresetId = 'basic_product' | 'product_without_pricing' | 'full' | 'custom';

export interface ApiKeyDataScope {
  preset?: ApiKeyScopePresetId;
  brands?: string[];
  dataTypes?: string[];
  productTypes?: string[];
}

export interface ApiKeyScopePresetOption {
  id: ApiKeyScopePresetId;
  label: string;
  description: string;
  allowed: boolean;
}

export interface ApiKeyScopeOptions {
  enabled: boolean;
  presets: ApiKeyScopePresetOption[];
  brands: string[];
  productTypes: string[];
  dataTypes: string[];
}

const PRESET_LABELS: Record<Exclude<ApiKeyScopePresetId, 'custom'>, string> = {
  basic_product: 'Basic product data',
  product_without_pricing: 'Everything except pricing',
  full: 'Full organization scope',
};

const DATA_TYPE_LABELS: Record<string, string> = {
  product_catalog: 'Catalog',
  product_specs: 'Specs',
  product_images: 'Images',
  pricing_msrp: 'MSRP',
  pricing_map: 'MAP',
  pricing_dealer: 'Dealer cost',
  pricing_promotional: 'Promotions and rebates',
  rebate_rules: 'Rebate rules',
  inventory_levels: 'Inventory',
  release_schedule: 'Release dates',
  discontinued_info: 'Discontinued info',
};

export const dataTypeLabel = (type: string): string => DATA_TYPE_LABELS[type] || type;

/** One line for a key row: what the key can see beyond (never beyond) its organization. */
export function describeKeyDataScope(scope: ApiKeyDataScope | null | undefined): string {
  if (!scope) return 'Inherits organization scope';
  const preset = scope.preset && scope.preset !== 'custom' ? PRESET_LABELS[scope.preset] : null;
  const parts: string[] = [];
  if (preset) parts.push(preset);
  else if (scope.dataTypes && scope.dataTypes.length > 0) parts.push(scope.dataTypes.map(dataTypeLabel).join(', '));
  if (scope.brands && scope.brands.length > 0) parts.push(scope.brands.join(', '));
  if (scope.productTypes && scope.productTypes.length > 0) parts.push(scope.productTypes.join(', '));
  return parts.length > 0 ? parts.join(' · ') : 'Inherits organization scope';
}
